<template>
  <div>
    <div class="flex flex-wrap items-center justify-between mb-4">
      <h4 class="mb-0">{{ $t('subscription.detail_title_management') }} #{{ id }}</h4>
      <router-link to="/subscriptions" class="text-primary">
        <feather-icon icon="ArrowLeftIcon" class="h-4 w-4 mr-1 inline" />
        {{ $t('subscription.back_to_list_management') }}
      </router-link>
    </div>

    <vs-tabs :color="colorx" v-model="tabIndex">
      <vs-tab :label="$t('subscription.tab_summary')">
        <vx-card class="mt-4">
          <div v-if="loading" class="text-center py-8">{{ $t('common.loading') }}</div>
          <div v-else-if="error" class="text-danger">{{ error }}</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <p class="text-sm text-grey">{{ $t('subscription.user') }}</p>
              <p class="font-medium">
                <span v-if="subscription.user">{{ subscription.user.name }}</span>
                <span v-else>—</span>
                <span v-if="subscription.user && subscription.user.email" class="text-sm text-grey">
                  · {{ subscription.user.email }}
                </span>
              </p>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.plan') }}</p>
              <p class="font-medium">{{ subscription.subscription_plan ? subscription.subscription_plan.name : '—' }}</p>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.status') }}</p>
              <vs-chip transparent :color="getSubscriptionStatusColor(subscription.status)" class="text-base">
                {{ subscription.status_label }}
              </vs-chip>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.payment_method') }}</p>
              <p>{{ paymentMethodLabel }}</p>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.next_billing_at') }}</p>
              <p>{{ formatDate(subscription.next_billing_at) }}</p>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.started_at') }}</p>
              <p>{{ formatDate(subscription.started_at) }}</p>
            </div>
            <div>
              <p class="text-sm text-grey">{{ $t('subscription.ends_at') }}</p>
              <p>{{ formatDate(subscription.ends_at) }}</p>
            </div>
            <div class="md:col-span-2">
              <p class="text-sm text-grey">{{ $t('subscription.latest_invoice') }}</p>
              <p>{{ subscription.latest_invoice_status_label || '—' }}</p>
            </div>
            <div class="md:col-span-2">
              <p class="text-sm text-grey">{{ $t('subscription.last_paid_invoice') }}</p>
              <p>{{ subscription.last_paid_invoice_display || '—' }}</p>
            </div>
          </div>
        </vx-card>
      </vs-tab>

      <vs-tab :label="$t('subscription.tab_invoices')">
        <vx-card class="mt-4">
          <div v-if="loading" class="text-center py-8">{{ $t('common.loading') }}</div>
          <div v-else-if="!invoices.length" class="text-center py-8 text-grey">
            {{ $t('msg_none') }}
          </div>
          <vs-table v-else :data="invoices" class="mt-2">
            <template slot="thead">
              <vs-th>{{ $t('subscription.invoice_period') }}</vs-th>
              <vs-th>{{ $t('subscription.invoice_amount') }}</vs-th>
              <vs-th>{{ $t('subscription.invoice_due_at') }}</vs-th>
              <vs-th>{{ $t('subscription.invoice_paid_at') }}</vs-th>
              <vs-th>{{ $t('subscription.status') }}</vs-th>
              <vs-th>{{ $t('subscription.invoice_payment_method') }}</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr :key="row.id" v-for="row in data">
                <vs-td>{{ row.period_index != null ? row.period_index : '—' }}</vs-td>
                <vs-td>{{ row.amount_display }}</vs-td>
                <vs-td>{{ formatDate(row.due_at) }}</vs-td>
                <vs-td>{{ formatDate(row.paid_at) }}</vs-td>
                <vs-td>
                  <vs-chip transparent :color="getInvoiceStatusColor(row.status)" class="text-sm">
                    {{ row.status_label }}
                  </vs-chip>
                </vs-td>
                <vs-td>{{ row.payment_method_label }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
      </vs-tab>

      <vs-tab label="Grade de horários">
        <subscription-slots v-if="tabIndex === 2" :id="id"/>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
import SubscriptionService from '@/services/api/SubscriptionService'
import SubscriptionSlots from './Slots.vue'

export default {
  components: { SubscriptionSlots },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data: () => ({
    service: null,
    colorx: 'primary',
    tabIndex: 0,
    loading: true,
    error: null,
    subscription: {},
    invoices: []
  }),
  computed: {
    paymentMethodLabel() {
      const pm = this.subscription.payment_method
      if (!pm || !pm.label) return '—'
      return pm.label
    }
  },
  methods: {
    formatDate(val) {
      if (!val) return '—'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleString('pt-BR')
    },
    getSubscriptionStatusColor(status) {
      const statusMap = {
        PE: 'warning',
        AC: 'success',
        CA: 'danger',
        EX: 'dark',
        SP: 'primary'
      }
      return statusMap[status] || 'default'
    },
    getInvoiceStatusColor(status) {
      const map = {
        paid: 'success',
        scheduled: 'primary',
        pending: 'warning',
        failed: 'danger',
        overdue: 'danger',
        canceled: 'dark',
        suspended: 'warning',
        unknown: 'default'
      }
      return map[status] || 'default'
    },
    load() {
      this.loading = true
      this.error = null
      this.service
        .find(Number(this.id))
        .then(res => {
          this.subscription = res || {}
          this.invoices = res.invoices || []
        })
        .catch(err => {
          this.error = err?.message || this.$t('messages.error')
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  beforeMount() {
    this.service = SubscriptionService.build(this.$vs)
  },
  mounted() {
    this.load()
  },
  watch: {
    id() {
      this.load()
    }
  }
}
</script>
